/**
 * Immutable shim package - provides a common interface for immutable data structures
 * using native JavaScript data structures with immutable-style APIs.
 */

// Import types for annotations
import type {
  ICollection,
  IList,
  IMap,
  IOrderedMap,
  IOrderedSet,
  IRecord,
  ISet,
  RecordFactory,
} from './internal/interfaces';
// oxlint-disable-next-line import/no-namespace
import * as NativeImpl from './internal/native-impl';

// Export interface types
export type {
  IMap,
  IList,
  ISet,
  IRecord,
  IOrderedMap,
  IOrderedSet,
  ISeq,
  RecordFactory,
  RecordOf,
  FromJS,
  Collection as ICollection,
} from './internal/interfaces';

// ============================================================================
// Export Native Implementation
// ============================================================================

// Factory functions with static methods (centralized augmentation)
export const Map = Object.assign(NativeImpl.Map, {
  isMap: NativeImpl.isMap,
});

export const List = Object.assign(NativeImpl.List, {
  of: <T>(...values: T[]): IList<T> => NativeImpl.List(values),
  isList: NativeImpl.isList,
});

export const Set = Object.assign(NativeImpl.Set, {
  of: <T>(...values: T[]): ISet<T> => NativeImpl.Set(values),
  isSet: NativeImpl.isSet,
});

export const Record = NativeImpl.Record;
export const OrderedMap = NativeImpl.OrderedMap;

export const OrderedSet = Object.assign(NativeImpl.OrderedSet, {
  of: <T>(...values: T[]): IOrderedSet<T> => NativeImpl.OrderedSet(values),
  isOrderedSet: NativeImpl.isOrderedSet,
});

// Type aliases matching factory names (enables: type M = Map<K, V>)
export type Map<K = unknown, V = unknown> = IMap<K, V>;
export type List<T = unknown> = IList<T>;
export type Set<T = unknown> = ISet<T>;
export type Record<T = unknown> = IRecord<T>;
export type OrderedMap<K = unknown, V = unknown> = IOrderedMap<K, V>;
export type OrderedSet<T = unknown> = IOrderedSet<T>;
export type Collection<K = unknown, V = unknown> = ICollection<K, V>;

// Static functions
// fromJS returns 'any' to match immutable.js - conversion is dynamic and users provide target type
export const fromJS: (value: any) => any = NativeImpl.fromJS;
export const is = NativeImpl.is;
export const isImmutable = NativeImpl.isImmutable;

// Legacy helpers (for compatibility)
// Properly typed get function - returns any to maintain compatibility
// The get function from immutable.js doesn't provide strong typing anyway
export const get: <K = any>(collection: any, key: K, notSetValue?: any) => any = NativeImpl.get;

export const getIn = NativeImpl.getIn;
export const set = NativeImpl.set;
export const setIn = NativeImpl.setIn;
export const Collection = NativeImpl.Collection;
export const Seq = NativeImpl.Seq;
export const Iterable = NativeImpl.Iterable;

// Namespace declarations for Collection and Seq types (used by consumer code)
// oxlint-disable-next-line typescript/no-namespace
export namespace Collection {
  export type Keyed<K, V> = IMap<K, V>;
  export type Indexed<T> = IList<T>;
}

// oxlint-disable-next-line typescript/no-namespace
export namespace Seq {
  export type Indexed<T> = IList<T>;
  export type Keyed<K, V> = IMap<K, V>;
}

// oxlint-disable-next-line typescript/no-namespace
export namespace Record {
  export type Factory<T> = RecordFactory<T>;
}
